import React, { useMemo } from 'react';
import { Holding, Loan, LiquidityEvent, Currency } from '../types';
import { convertCurrency, formatCurrency } from '../utils/currency';
import { calculateMonthlyPayment, calculateTotalInterest } from '../utils/loans';
import { Card, CardHeader, CardContent } from './ui/Card';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { Calendar, Clock } from 'lucide-react';
interface DashboardChartsProps {
  holdings: Holding[];
  loans: Loan[];
  liquidityEvents: LiquidityEvent[];
  baseCurrency: Currency;
  partnerNames: [string, string];
  monthlyContribution?: number;
}
export function DashboardCharts({
  holdings,
  loans,
  liquidityEvents,
  baseCurrency,
  partnerNames,
  monthlyContribution = 0
}: DashboardChartsProps) {
  // 1. Top Holdings
  const topHoldings = useMemo(() => {
    return holdings.map(h => ({
      ...h,
      convertedAmount: convertCurrency(h.amount, h.currency, baseCurrency)
    })).sort((a, b) => b.convertedAmount - a.convertedAmount).slice(0, 10);
  }, [holdings, baseCurrency]);
  const totalAssets = useMemo(() => {
    return holdings.reduce((sum, h) => sum + convertCurrency(h.amount, h.currency, baseCurrency), 0);
  }, [holdings, baseCurrency]);
  // 2. Net Worth Timeline
  const timelineData = useMemo(() => {
    // Collect all unique dates from all histories
    const allDates = new Set<string>();
    holdings.forEach(h => {
      if (h.history) {
        h.history.forEach(entry => {
          // Truncate to day for grouping
          const date = new Date(entry.date).toISOString().split('T')[0];
          allDates.add(date);
        });
      }
    });
    const sortedDates = Array.from(allDates).sort();
    if (sortedDates.length === 0) return [];
    // For each date, calculate total net worth
    return sortedDates.map(dateStr => {
      const targetDate = new Date(dateStr).getTime();
      const totalValue = holdings.reduce((sum, h) => {
        if (!h.history || h.history.length === 0) {
          // If no history, assume current amount has always been the amount
          // (or 0 if we want to be strict, but using current amount is better for UX if history is missing)
          return sum + convertCurrency(h.amount, h.currency, baseCurrency);
        }
        // Find the latest entry on or before this date
        let latestAmount = 0;
        let found = false;
        // History is usually chronological, but let's sort to be safe
        const sortedHistory = [...h.history].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
        for (const entry of sortedHistory) {
          if (new Date(entry.date).getTime() <= targetDate) {
            latestAmount = entry.amount;
            found = true;
          } else {
            break; // We passed the target date
          }
        }
        // If no entry found before this date, it means the asset didn't exist yet
        if (!found) return sum;
        return sum + convertCurrency(latestAmount, h.currency, baseCurrency);
      }, 0);
      return {
        date: new Date(dateStr).toLocaleDateString(undefined, {
          month: 'short',
          day: 'numeric',
          year: 'numeric'
        }),
        value: totalValue
      };
    });
  }, [holdings, baseCurrency]);
  // 3. Debt Overview
  const activeLoans = useMemo(() => loans.filter(l => l.isActive), [loans]);
  const debtSummary = useMemo(() => {
    let totalPrincipal = 0;
    let totalMonthly = 0;
    let totalInterest = 0;
    let totalDeposits = 0;
    activeLoans.forEach(loan => {
      const principal = Math.max(0, loan.totalAmount - loan.depositAmount);
      const monthly = calculateMonthlyPayment(loan);
      const interest = calculateTotalInterest(loan);
      totalPrincipal += convertCurrency(principal, loan.currency, baseCurrency);
      totalMonthly += convertCurrency(monthly, loan.currency, baseCurrency);
      totalInterest += convertCurrency(interest, loan.currency, baseCurrency);
      if (loan.depositAmount > 0) {
        totalDeposits += convertCurrency(loan.depositAmount, loan.currency, baseCurrency);
      }
    });
    // Calculate debt-free date (latest loan end date)
    const now = new Date();
    let latestEndDate: Date | null = null;
    activeLoans.forEach(loan => {
      const endDate = new Date(now);
      endDate.setFullYear(endDate.getFullYear() + loan.startYear);
      endDate.setMonth(endDate.getMonth() + loan.durationMonths);
      if (!latestEndDate || endDate > latestEndDate) {
        latestEndDate = endDate;
      }
    });
    return {
      totalPrincipal,
      totalMonthly,
      totalInterest,
      totalDeposits,
      debtFreeDate: latestEndDate
    };
  }, [activeLoans, baseCurrency]);
  // Per-loan date info
  const loanDates = useMemo(() => {
    const now = new Date();
    return activeLoans.map(loan => {
      const startDate = new Date(now);
      startDate.setFullYear(startDate.getFullYear() + loan.startYear);
      const endDate = new Date(startDate);
      endDate.setMonth(endDate.getMonth() + loan.durationMonths);
      return {
        id: loan.id,
        startDate,
        endDate
      };
    });
  }, [activeLoans]);
  // 4. Monthly Cash Flow — period-based
  const cashFlowPeriods = useMemo(() => {
    // Collect all year breakpoints where cash flow changes
    const breakpoints = new Set<number>();
    breakpoints.add(0); // "Now"
    const activeEvents = liquidityEvents.filter(e => (e.isActive ?? true) && e.type !== 'one-time');
    activeEvents.forEach(e => {
      if (e.startYear > 0) breakpoints.add(e.startYear);
      if (e.endYear !== undefined) breakpoints.add(e.endYear + 1);
    });
    activeLoans.forEach(loan => {
      if (loan.startYear > 0) breakpoints.add(loan.startYear);
      const endYear = loan.startYear + Math.ceil(loan.durationMonths / 12);
      breakpoints.add(endYear);
    });
    const sortedBreakpoints = Array.from(breakpoints).sort((a, b) => a - b);
    // Only show up to ~30 years out
    const filtered = sortedBreakpoints.filter(y => y <= 30);
    // Build periods
    const periods: Array<{
      label: string;
      yearStart: number;
      yearEnd: number | null;
      inflows: number;
      outflows: number;
      net: number;
    }> = [];
    for (let i = 0; i < filtered.length; i++) {
      const yearStart = filtered[i];
      const yearEnd = i + 1 < filtered.length ? filtered[i + 1] : null;
      let inflows = 0;
      let outflows = 0;
      // Monthly contribution (assumed perpetual from now)
      if (monthlyContribution > 0) {
        inflows += monthlyContribution;
      } else if (monthlyContribution < 0) {
        outflows += Math.abs(monthlyContribution);
      }
      // Events active during this period
      activeEvents.forEach(e => {
        const eventStart = e.startYear;
        const eventEnd = e.endYear ?? Infinity;
        if (yearStart >= eventStart && yearStart <= eventEnd) {
          let monthlyAmount = 0;
          if (e.type === 'monthly') {
            monthlyAmount = e.amount;
          } else if (e.type === 'yearly') {
            monthlyAmount = e.amount / 12;
          }
          const converted = convertCurrency(monthlyAmount, e.currency, baseCurrency);
          if (converted > 0) {
            inflows += converted;
          } else {
            outflows += Math.abs(converted);
          }
        }
      });
      // Loans active during this period
      activeLoans.forEach(loan => {
        const loanStart = loan.startYear;
        const loanEnd = loan.startYear + Math.ceil(loan.durationMonths / 12);
        if (yearStart >= loanStart && yearStart < loanEnd) {
          const monthly = calculateMonthlyPayment(loan);
          outflows += convertCurrency(monthly, loan.currency, baseCurrency);
        }
      });
      // Build label
      const now = new Date();
      const startDate = new Date(now);
      startDate.setFullYear(startDate.getFullYear() + yearStart);
      let label: string;
      if (yearStart === 0 && yearEnd !== null) {
        const endDate = new Date(now);
        endDate.setFullYear(endDate.getFullYear() + yearEnd);
        label = `Now → ${endDate.toLocaleDateString(undefined, {
          month: 'short',
          year: 'numeric'
        })}`;
      } else if (yearEnd !== null) {
        const endDate = new Date(now);
        endDate.setFullYear(endDate.getFullYear() + yearEnd);
        label = `${startDate.toLocaleDateString(undefined, {
          month: 'short',
          year: 'numeric'
        })} → ${endDate.toLocaleDateString(undefined, {
          month: 'short',
          year: 'numeric'
        })}`;
      } else {
        label = `From ${startDate.toLocaleDateString(undefined, {
          month: 'short',
          year: 'numeric'
        })}`;
      }
      periods.push({
        label,
        yearStart,
        yearEnd,
        inflows,
        outflows,
        net: inflows - outflows
      });
    }
    // Deduplicate periods with identical cash flows
    const deduped: typeof periods = [];
    for (const period of periods) {
      const prev = deduped[deduped.length - 1];
      if (prev && Math.abs(prev.inflows - period.inflows) < 0.01 && Math.abs(prev.outflows - period.outflows) < 0.01) {
        // Merge: extend the previous period
        prev.yearEnd = period.yearEnd;
        // Update label
        const now = new Date();
        const startDate = new Date(now);
        startDate.setFullYear(startDate.getFullYear() + prev.yearStart);
        if (prev.yearStart === 0 && period.yearEnd !== null) {
          const endDate = new Date(now);
          endDate.setFullYear(endDate.getFullYear() + period.yearEnd);
          prev.label = `Now → ${endDate.toLocaleDateString(undefined, {
            month: 'short',
            year: 'numeric'
          })}`;
        } else if (period.yearEnd !== null) {
          const endDate = new Date(now);
          endDate.setFullYear(endDate.getFullYear() + period.yearEnd);
          prev.label = `${startDate.toLocaleDateString(undefined, {
            month: 'short',
            year: 'numeric'
          })} → ${endDate.toLocaleDateString(undefined, {
            month: 'short',
            year: 'numeric'
          })}`;
        } else {
          prev.label = `From ${startDate.toLocaleDateString(undefined, {
            month: 'short',
            year: 'numeric'
          })}`;
        }
      } else {
        deduped.push({
          ...period
        });
      }
    }
    return deduped;
  }, [liquidityEvents, activeLoans, baseCurrency, monthlyContribution]);
  const CustomTooltip = ({
    active,
    payload,
    label
  }: any) => {
    if (active && payload && payload.length) {
      return <div className="bg-white p-3 border border-slate-200 shadow-lg rounded-lg" data-id="element-75">
          <p className="text-sm font-medium text-slate-500 mb-1" data-id="element-76">{label}</p>
          <p className="text-sm font-bold text-slate-900" data-id="element-77">
            {formatCurrency(payload[0].value, baseCurrency)}
          </p>
        </div>;
    }
    return null;
  };
  return <div className="space-y-8" data-id="element-78">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-79">
        {/* Net Worth Timeline */}
        <Card className="flex flex-col" data-id="element-80">
          <CardHeader title="Net Worth History" subtitle="Based on recorded changes to your holdings" data-id="element-81" />
          <CardContent className="flex-1 min-h-[300px] flex flex-col justify-center" data-id="element-82">
            {timelineData.length < 2 ? <div className="text-center text-slate-400 py-12" data-id="element-83">
                <p data-id="element-84">Not enough history data yet.</p>
                <p className="text-sm mt-1" data-id="element-85">
                  Update your holding amounts over time to see the trend.
                </p>
              </div> : <div className="h-[250px] w-full mt-4" data-id="element-86">
                <ResponsiveContainer width="100%" height="100%" data-id="element-87">
                  <AreaChart data={timelineData} margin={{
                top: 10,
                right: 10,
                left: 0,
                bottom: 0
              }} data-id="element-88">
                    <defs data-id="element-89">
                      <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1" data-id="element-90">
                        <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} data-id="element-91" />
                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} data-id="element-92" />
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{
                  fill: '#94a3b8',
                  fontSize: 12
                }} dy={10} minTickGap={30} data-id="element-93" />
                    <YAxis hide domain={['dataMin - (dataMax - dataMin) * 0.1', 'dataMax + (dataMax - dataMin) * 0.1']} data-id="element-94" />
                    <Tooltip content={<CustomTooltip data-id="element-96" />} data-id="element-95" />
                    <Area type="monotone" dataKey="value" stroke="#3b82f6" strokeWidth={2} fillOpacity={1} fill="url(#colorValue)" data-id="element-97" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>}
          </CardContent>
        </Card>

        {/* Top Holdings */}
        <Card className="flex flex-col" data-id="element-98">
          <CardHeader title="Top Holdings" subtitle="Largest assets in your portfolio" data-id="element-99" />
          <CardContent className="flex-1" data-id="element-100">
            {topHoldings.length === 0 ? <div className="text-center text-slate-400 py-12" data-id="element-101">
                <p data-id="element-102">No holdings added yet.</p>
              </div> : <div className="space-y-4" data-id="element-103">
                {topHoldings.map(holding => {
              const pct = totalAssets > 0 ? holding.convertedAmount / totalAssets * 100 : 0;
              const isFirstPartner = holding.person === partnerNames[0];
              const isJoint = holding.person === 'Joint';
              return <div key={holding.id} className="space-y-1.5" data-id="element-104">
                      <div className="flex justify-between items-end text-sm" data-id="element-105">
                        <div className="flex items-center gap-2 truncate pr-4" data-id="element-106">
                          <span className="font-medium text-slate-800 truncate" data-id="element-107">
                            {holding.name}
                          </span>
                          <span className={`shrink-0 text-[10px] px-1.5 py-0.5 rounded font-medium ${isJoint ? 'bg-teal-50 text-teal-700' : isFirstPartner ? 'bg-indigo-50 text-indigo-700' : 'bg-fuchsia-50 text-fuchsia-700'}`} data-id="element-108">
                            {isJoint ? 'Mutual' : holding.person}
                          </span>
                        </div>
                        <div className="text-right shrink-0" data-id="element-109">
                          <span className="font-semibold text-slate-900" data-id="element-110">
                            {formatCurrency(holding.convertedAmount, baseCurrency)}
                          </span>
                          <span className="text-slate-400 ml-2 w-10 inline-block text-right" data-id="element-111">
                            {pct.toFixed(1)}%
                          </span>
                        </div>
                      </div>
                      <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden" data-id="element-112">
                        <div className={`h-full rounded-full ${holding.type === 'Liquid' ? 'bg-blue-500' : 'bg-slate-500'}`} style={{
                    width: `${pct}%`
                  }} data-id="element-113" />
                      </div>
                    </div>;
            })}
              </div>}
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-114">
        {/* Monthly Cash Flow Summary — Period-based */}
        <Card className="flex flex-col" data-id="element-115">
          <CardHeader title="Monthly Cash Flow" subtitle="Recurring inflows & outflows by period" data-id="element-116" />
          <CardContent className="flex-1 flex flex-col justify-center" data-id="element-117">
            {cashFlowPeriods.length === 0 || cashFlowPeriods.length === 1 && cashFlowPeriods[0].inflows === 0 && cashFlowPeriods[0].outflows === 0 ? <div className="text-center text-slate-400 py-12" data-id="element-118">
                <p data-id="element-119">No recurring cash flows.</p>
                <p className="text-sm mt-1" data-id="element-120">
                  Add monthly events or loans to see your cash flow.
                </p>
              </div> : <div className="space-y-6" data-id="element-121">
                {cashFlowPeriods.map((period, idx) => {
              if (period.inflows === 0 && period.outflows === 0) return null;
              const total = period.inflows + period.outflows;
              return <div key={idx} className="space-y-3" data-id="element-122">
                      <div className="flex items-center gap-2" data-id="element-123">
                        <Clock className="w-3.5 h-3.5 text-slate-400" data-id="element-124" />
                        <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider" data-id="element-125">
                          {period.label}
                        </span>
                        {idx === 0 && <span className="text-[10px] font-semibold bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded" data-id="element-126">
                            Current
                          </span>}
                      </div>
                      <div className="grid grid-cols-3 gap-3 text-center" data-id="element-127">
                        <div className="p-3 bg-emerald-50 rounded-xl" data-id="element-128">
                          <p className="text-[10px] font-semibold text-emerald-600 uppercase tracking-wider mb-0.5" data-id="element-129">
                            Inflows
                          </p>
                          <p className="text-sm font-bold text-emerald-700" data-id="element-130">
                            +{formatCurrency(period.inflows, baseCurrency)}
                          </p>
                        </div>
                        <div className="p-3 bg-rose-50 rounded-xl" data-id="element-131">
                          <p className="text-[10px] font-semibold text-rose-600 uppercase tracking-wider mb-0.5" data-id="element-132">
                            Outflows
                          </p>
                          <p className="text-sm font-bold text-rose-700" data-id="element-133">
                            -{formatCurrency(period.outflows, baseCurrency)}
                          </p>
                        </div>
                        <div className={`p-3 rounded-xl ${period.net >= 0 ? 'bg-blue-50' : 'bg-slate-100'}`} data-id="element-134">
                          <p className={`text-[10px] font-semibold uppercase tracking-wider mb-0.5 ${period.net >= 0 ? 'text-blue-600' : 'text-slate-500'}`} data-id="element-135">
                            Net
                          </p>
                          <p className={`text-sm font-bold ${period.net >= 0 ? 'text-blue-700' : 'text-slate-700'}`} data-id="element-136">
                            {period.net > 0 ? '+' : ''}
                            {formatCurrency(period.net, baseCurrency)}
                          </p>
                        </div>
                      </div>
                      {total > 0 && <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden flex" data-id="element-137">
                          {period.inflows > 0 && <div className="h-full bg-emerald-400" style={{
                    width: `${period.inflows / total * 100}%`
                  }} data-id="element-138" />}
                          {period.outflows > 0 && <div className="h-full bg-rose-400" style={{
                    width: `${period.outflows / total * 100}%`
                  }} data-id="element-139" />}
                        </div>}
                      {idx < cashFlowPeriods.length - 1 && <div className="border-b border-dashed border-slate-200" data-id="element-140" />}
                    </div>;
            })}
              </div>}
          </CardContent>
        </Card>

        {/* Debt Overview */}
        <Card className="flex flex-col" data-id="element-141">
          <CardHeader title="Debt Overview" subtitle="Summary of your active loans and mortgages" data-id="element-142" />
          <CardContent className="flex-1" data-id="element-143">
            {activeLoans.length === 0 ? <div className="text-center text-slate-400 py-12" data-id="element-144">
                <p data-id="element-145">No active loans.</p>
                <p className="text-sm mt-1" data-id="element-146">You are completely debt-free!</p>
              </div> : <div className="space-y-6" data-id="element-147">
                <div className={`grid gap-4 ${debtSummary.totalDeposits > 0 ? 'grid-cols-2 sm:grid-cols-4' : 'grid-cols-3'}`} data-id="element-148">
                  <div data-id="element-149">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1" data-id="element-150">
                      Total Principal
                    </p>
                    <p className="text-lg font-bold text-slate-900" data-id="element-151">
                      {formatCurrency(debtSummary.totalPrincipal, baseCurrency)}
                    </p>
                  </div>
                  <div data-id="element-152">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1" data-id="element-153">
                      Total Interest
                    </p>
                    <p className="text-lg font-bold text-rose-600" data-id="element-154">
                      {formatCurrency(debtSummary.totalInterest, baseCurrency)}
                    </p>
                  </div>
                  <div data-id="element-155">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1" data-id="element-156">
                      Monthly Pmt
                    </p>
                    <p className="text-lg font-bold text-slate-700" data-id="element-157">
                      {formatCurrency(debtSummary.totalMonthly, baseCurrency)}
                    </p>
                  </div>
                  {debtSummary.totalDeposits > 0 && <div data-id="element-158">
                      <p className="text-xs font-semibold text-amber-600 uppercase tracking-wider mb-1" data-id="element-159">
                        Total Deposits
                      </p>
                      <p className="text-lg font-bold text-amber-700" data-id="element-160">
                        {formatCurrency(debtSummary.totalDeposits, baseCurrency)}
                      </p>
                    </div>}
                </div>

                {/* Debt Free By */}
                {debtSummary.debtFreeDate && <div className="flex items-center gap-3 p-3 bg-emerald-50 rounded-xl border border-emerald-100" data-id="element-161">
                    <div className="bg-emerald-100 p-2 rounded-lg" data-id="element-162">
                      <Calendar className="w-4 h-4 text-emerald-600" data-id="element-163" />
                    </div>
                    <div data-id="element-164">
                      <p className="text-xs font-semibold text-emerald-600 uppercase tracking-wider" data-id="element-165">
                        Debt Free By
                      </p>
                      <p className="text-sm font-bold text-emerald-800" data-id="element-166">
                        {debtSummary.debtFreeDate.toLocaleDateString(undefined, {
                    month: 'long',
                    year: 'numeric'
                  })}
                      </p>
                    </div>
                  </div>}

                <div className="space-y-4 pt-4 border-t border-slate-100" data-id="element-167">
                  <h4 className="text-sm font-semibold text-slate-700" data-id="element-168">
                    Loan Details
                  </h4>
                  {activeLoans.map(loan => {
                const principal = Math.max(0, loan.totalAmount - loan.depositAmount);
                const interest = calculateTotalInterest(loan);
                const total = principal + interest;
                const dates = loanDates.find(d => d.id === loan.id);
                if (total === 0) return null;
                const principalPct = principal / total * 100;
                const interestPct = interest / total * 100;
                return <div key={loan.id} className="space-y-1.5" data-id="element-169">
                        <div className="flex justify-between text-xs" data-id="element-170">
                          <span className="font-medium text-slate-700 truncate pr-4" data-id="element-171">
                            {loan.name}
                          </span>
                          <span className="text-slate-500 shrink-0" data-id="element-172">
                            {formatCurrency(convertCurrency(total, loan.currency, baseCurrency), baseCurrency)}{' '}
                            total
                          </span>
                        </div>
                        {dates && <div className="flex items-center gap-1.5 text-[11px] text-slate-400" data-id="element-173">
                            <Calendar className="w-3 h-3" data-id="element-174" />
                            <span data-id="element-175">
                              {dates.startDate.toLocaleDateString(undefined, {
                        month: 'short',
                        year: 'numeric'
                      })}
                              {' → '}
                              {dates.endDate.toLocaleDateString(undefined, {
                        month: 'short',
                        year: 'numeric'
                      })}
                            </span>
                            <span className="text-slate-300" data-id="element-176">·</span>
                            <span data-id="element-177">{loan.durationMonths} months</span>
                          </div>}
                        <div className="h-3 w-full bg-slate-100 rounded-full overflow-hidden flex" data-id="element-178">
                          <div className="h-full bg-slate-400" style={{
                      width: `${principalPct}%`
                    }} title={`Principal: ${formatCurrency(principal, loan.currency)}`} data-id="element-179" />
                          <div className="h-full bg-rose-400" style={{
                      width: `${interestPct}%`
                    }} title={`Interest: ${formatCurrency(interest, loan.currency)}`} data-id="element-180" />
                        </div>
                      </div>;
              })}
                </div>
              </div>}
          </CardContent>
        </Card>
      </div>
    </div>;
}